import {
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSElement,
  WSFlexBox,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayeeEngagement } from "@wingspanhq/wingspan-api-sdk";
import { queriesPayeeEngagements } from "@query";
import { ErrorAlert } from "@components/ErrorAlert";
import { FieldView } from "../../../shared/components/FieldView/FieldView";
import React from "react";

export interface RestoreEngagementModalProps {
  payeeId: string;
  payeeName: string;
  engagement: PayeeEngagement;
  onClose: () => void;
}

export const RestoreEngagementModal: React.FC<RestoreEngagementModalProps> = ({
  payeeId,
  payeeName,
  engagement,
  onClose
}) => {
  const { openSnackbar } = useWSSnackbar();
  // v3 lifecycle is action-verb driven; `Suspended → Activated` is the documented
  // side path back (`.claude/rules/status-machines.md`).
  const [activateEngagement, activateEngagementMeta] =
    queriesPayeeEngagements.activatePayeeEngagement.useMutation();

  return (
    <>
      <WSText.ParagraphSm mb="L" weight="book">
        Are you sure you want to restore this assignment?
      </WSText.ParagraphSm>
      <WSPanel>
        <WSFlexBox gap="XL">
          <FieldView
            label="Assignment name"
            value={engagement.engagementName}
          />
          <FieldView label="Contractor name" value={payeeName} />
        </WSFlexBox>
      </WSPanel>

      <WSAlert
        my="2XL"
        theme="info"
        icon="info-circle"
        size="M"
        title="Restoring an assignment - what to expect:"
      >
        <WSElement as="ul" ml="L">
          <li>New invoices and payments can be created for this assignment.</li>
          <li>
            Eligibility state for the assignment will be restored (including any
            changes that have occurred for eligibility requirements).
          </li>
        </WSElement>
      </WSAlert>

      <ErrorAlert
        error={activateEngagementMeta.error}
        mb="XL"
        context="ArchiveClient"
      />

      <WSActions
        alignment="fill"
        buttons={[
          {
            label: "Cancel",
            onClick: onClose,
            kind: "Secondary"
          },
          {
            label: "Restore assignment",
            loading: activateEngagementMeta.isLoading,
            kind: "Primary",
            onAsyncClick: async () => {
              await activateEngagement(
                {
                  payeeId,
                  payeeEngagementId: engagement.id as string
                },
                {
                  onSuccess: () => {
                    openSnackbar({
                      theme: "dark",
                      type: "success",
                      message: "Assignment restored"
                    });
                    onClose();
                  }
                }
              );
            }
          }
        ]}
      />
    </>
  );
};

export const useRestoreEngagementModal = () => {
  return useWSModal(RestoreEngagementModal, {
    title: "Restore assignment",
    size: "M"
  });
};
